// create page

import React from 'react';
import {
  Box,
  Container,
  GlobalStyles,
  Grid,
  Stack,
  Skeleton,
  Typography,
} from '@mui/material';
import { ReactComponent as ZicassoLogo } from '../../assets/svg/zicasso_logo_black.svg';
import userIconUrl from '../../assets/svg/user-nav.svg';
import { TripHeroImage } from '../../components/trips';

const APP_BAR_HEIGHT = '68px';

const NAV_LINK_STYLE = {
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.56px',
  textTransform: 'uppercase',
  color: '#333',
} as const;

const SkeletonInfoBox = (): React.ReactElement => (
  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
    <Typography variant="h6">
      <Skeleton width="50%" />
    </Typography>
    <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '70%' }} />
    <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '90%' }} />
    <Skeleton variant="rectangular" width={150} height={36} sx={{ marginTop: 2, borderRadius: 1 }} />
  </Box>
);

const SkeletonTripDetailsBackground = ({ photoId, destinations }: { photoId: string | null, destinations: string | null }): React.ReactElement => (
  <>
    <GlobalStyles styles={{ html: { scrollbarGutter: 'auto !important' } }} />
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 1100,
        backgroundColor: '#F0ECE9',
        height: APP_BAR_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        boxSizing: 'border-box',
        '@media (min-width: 1100px)': {
          padding: '0 80px',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1568px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <ZicassoLogo style={{ width: 112, height: 20 }} />
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: { md: '1.5rem', lg: '2rem' },
          }}
        >
          {['Destinations', 'Experiences', 'Inspiration', 'About'].map((item) => (
            <Box
              key={item}
              component="span"
              sx={NAV_LINK_STYLE}
            >
              {item}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <Box
            component="span"
            sx={{ ...NAV_LINK_STYLE, display: { xs: 'none', lg: 'block' } }}
          >
            888 441-2418
          </Box>
          <Box component="span" sx={NAV_LINK_STYLE}>
            MY TRIPS
          </Box>
          <Box
            component="img"
            src={userIconUrl}
            alt=""
            aria-hidden="true"
            sx={{ height: '1.5rem', width: '1.5rem' }}
          />
        </Box>
      </Box>
    </Box>
    <Container
      maxWidth="xl"
      sx={{
        mt: APP_BAR_HEIGHT,
        minHeight: `calc(100vh - ${APP_BAR_HEIGHT})`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ py: 2, flexGrow: 1 }}>
        <Grid
          container
          spacing={{
            desktop: 6,
            mobile: 4,
          }}
        >
          <Grid item xs={12} sm={12} md={6}>
            {photoId ? (
              <Box
                sx={{
                  width: '100%',
                  height: '80vh',
                  marginTop: 1,
                  borderRadius: '16px',
                  overflow: 'hidden',
                }}
              >
                <TripHeroImage
                  photoId={photoId}
                />
              </Box>
            ) : (
              <Skeleton
                variant="rectangular"
                sx={{
                  width: '100%',
                  height: '80vh',
                  marginTop: 1,
                  borderRadius: '16px',
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Stack
              spacing={4}
              sx={{
                flex: { desktop: '1 1 auto' },
                maxWidth: {
                  desktop: '642px',
                },
                mb: 6,
              }}
            >
              {/* Mock Trip Title Area */}
              <Box>
                <Typography sx={{ marginBottom: 0.75 }} variant="subtitle1" color="text.secondary">Trip To</Typography>
                <Typography
                  data-e2e="ztest-trip-to"
                  variant="h2"
                  sx={{
                    marginBottom: 2,
                  }}
                >
                  {destinations || <Skeleton width={200} />}
                </Typography>
                <Skeleton variant="rectangular" width={60} height={22} sx={{ marginTop: 1, borderRadius: '16px' }} />
              </Box>

              {/* Mock Booking Details Area */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  <Skeleton width="40%" />
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body1">
                    <strong>
                      <Skeleton width="25%" />
                    </strong>
                    {' '}
                    <Skeleton width="50%" />
                  </Typography>
                  <Typography variant="body1">
                    <strong>
                      <Skeleton width="45%" />
                    </strong>
                    {' '}
                    <Skeleton width="20%" />
                  </Typography>
                </Stack>
              </Box>
              <SkeletonInfoBox />
              <SkeletonInfoBox />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Container>
  </>
);

export default SkeletonTripDetailsBackground;
